import { Directive, ElementRef, Input, OnInit, OnDestroy, NgZone } from '@angular/core';

/**
 * Flutter BouncingScrollPhysics benzeri elastic overscroll efekti.
 * Kullanım: <div fikoBounceScroll> veya <div fikoBounceScroll="horizontal">
 *
 * Flutter davranış garantileri:
 * ─ İç scrollable child sınırına ulaşmadan üst elemana taşmaz
 * ─ Dialog/drawer/modal içindeyken arka planı etkilemez
 * ─ CDK drag-drop ve diğer sürükleme işlemleriyle çakışmaz
 * ─ Sadece kendi scroll sınırında elastic bounce uygular
 */
@Directive({
  selector: '[fikoBounceScroll]',
  standalone: true,
})
export class BounceScrollDirective implements OnInit, OnDestroy {
  /** 'vertical' (varsayılan) | 'horizontal' | 'both' */
  @Input('fikoBounceScroll') yon: string = '';

  /** Maksimum elastic uzama mesafesi (px) */
  @Input() bounceMax = 120;

  /** Direnç katsayısı (0-1 arası, düşük = daha sert) */
  @Input() bounceDamping = 0.4;

  /** Spring-back animasyon süresi (ms) */
  @Input() bounceSpeed = 400;

  private el: HTMLElement;

  // Pointer tracking
  private _startX = 0;
  private _startY = 0;
  private _isDragging = false;
  private _isBouncing = false;
  private _overflowX = 0;
  private _overflowY = 0;
  private _rafId = 0;
  private _springRafId = 0;

  // Wheel
  private _wheelOverflowY = 0;
  private _wheelOverflowX = 0;
  private _wheelTimer: any = null;

  // Mouse
  private _mouseTarget = false;

  // Bound listeners
  private _onTouchStart: (e: TouchEvent) => void;
  private _onTouchMove: (e: TouchEvent) => void;
  private _onTouchEnd: () => void;
  private _onMouseDown: (e: MouseEvent) => void;
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseUp: () => void;
  private _onWheel: (e: WheelEvent) => void;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    this.el = this.elRef.nativeElement;
    if (!this.yon || this.yon === '') this.yon = 'vertical';

    this.el.style.overscrollBehavior = 'none';

    this.zone.runOutsideAngular(() => {
      this._onTouchStart = this._handleTouchStart.bind(this);
      this._onTouchMove = this._handleTouchMove.bind(this);
      this._onTouchEnd = this._handlePointerEnd.bind(this);
      this._onMouseDown = this._handleMouseDown.bind(this);
      this._onMouseMove = this._handleMouseMove.bind(this);
      this._onMouseUp = this._handlePointerEnd.bind(this);
      this._onWheel = this._handleWheel.bind(this);

      this.el.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.el.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.el.addEventListener('touchend', this._onTouchEnd, { passive: true });
      this.el.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
      this.el.addEventListener('mousedown', this._onMouseDown, { passive: true });
      document.addEventListener('mousemove', this._onMouseMove, { passive: false });
      document.addEventListener('mouseup', this._onMouseUp, { passive: true });
      this.el.addEventListener('wheel', this._onWheel, { passive: false });
    });
  }

  ngOnDestroy() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._springRafId) cancelAnimationFrame(this._springRafId);
    clearTimeout(this._wheelTimer);

    this.el.removeEventListener('touchstart', this._onTouchStart);
    this.el.removeEventListener('touchmove', this._onTouchMove);
    this.el.removeEventListener('touchend', this._onTouchEnd);
    this.el.removeEventListener('touchcancel', this._onTouchEnd);
    this.el.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.el.removeEventListener('wheel', this._onWheel);
  }

  // ═══════════════════════════════════════════════════
  //  FLUTTER GESTURE ARENA — Olayı bize mi ait kontrol
  // ═══════════════════════════════════════════════════

  /**
   * Event target ile bu element arasında scrollable bir child var mı?
   * Varsa ve o child henüz sınırına ulaşmamışsa → event o child'a ait, biz karışmayız.
   */
  private _icScrollKontrol(target: EventTarget | null, deltaX: number, deltaY: number): boolean {
    let node = target as HTMLElement | null;
    while (node && node !== this.el) {
      // Scrollable mı kontrol et
      if (this._elemanScrollable(node, deltaX, deltaY)) {
        return true; // İç eleman hala scroll edebilir — biz karışmıyoruz
      }
      node = node.parentElement;
    }
    return false;
  }

  /** Bir elemanın verilen yönde hala scroll edebilir olup olmadığını kontrol eder */
  private _elemanScrollable(el: HTMLElement, deltaX: number, deltaY: number): boolean {
    const style = getComputedStyle(el);

    // Dikey scroll kontrolü
    if (this.yon === 'vertical' || this.yon === 'both') {
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        const scrollableY = el.scrollHeight > el.clientHeight + 1;
        if (scrollableY) {
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
          // Yukarı scroll ve tepede değil, veya aşağı scroll ve dipte değil → child scroll edebilir
          if (deltaY < 0 && !atTop) return true;
          if (deltaY > 0 && !atBottom) return true;
          // Child sınırda ama içerik var → child'ın bounce scroll'u varsa ona bırak
          if (el.hasAttribute('fikobouncesscroll') || el.hasAttribute('fikoBounceScroll')) return true;
        }
      }
    }

    // Yatay scroll kontrolü
    if (this.yon === 'horizontal' || this.yon === 'both') {
      const overflowX = style.overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
        const scrollableX = el.scrollWidth > el.clientWidth + 1;
        if (scrollableX) {
          const atLeft = el.scrollLeft <= 0;
          const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
          if (deltaX < 0 && !atLeft) return true;
          if (deltaX > 0 && !atRight) return true;
          if (el.hasAttribute('fikobouncesscroll') || el.hasAttribute('fikoBounceScroll')) return true;
        }
      }
    }

    return false;
  }

  /**
   * Event dialog/drawer/modal overlay içinden mi geliyor?
   * Evetse bounce scroll uygulamıyoruz.
   */
  private _dialogIcindeMi(target: EventTarget | null): boolean {
    let node = target as HTMLElement | null;
    while (node) {
      // Bu bizim element'imizse, dialog içinde değiliz
      if (node === this.el) return false;
      // Ng-Zorro / CDK overlay kontrolleri
      const cls = node.classList;
      if (
        cls?.contains('ant-modal-wrap') ||
        cls?.contains('ant-modal-content') ||
        cls?.contains('ant-drawer-wrapper-body') ||
        cls?.contains('ant-drawer-content') ||
        cls?.contains('cdk-overlay-container') ||
        cls?.contains('cdk-overlay-pane') ||
        cls?.contains('ant-popover-inner') ||
        cls?.contains('ant-dropdown') ||
        cls?.contains('ant-select-dropdown') ||
        node.tagName === 'NZ-MODAL-CONTAINER'
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  /**
   * CDK drag-drop veya diğer sürükleme operasyonu aktif mi?
   * Aktifse bounce scroll devre dışı kalır.
   */
  private _dragAktifMi(target: EventTarget | null): boolean {
    // CDK drag: body'de .cdk-drag-preview veya .cdk-drag-placeholder varsa drag aktif
    if (document.querySelector('.cdk-drag-preview')) return true;

    // Target veya üst elemanlarında cdkDragHandle / [cdkDrag] varsa
    let node = target as HTMLElement | null;
    while (node && node !== this.el) {
      if (
        node.hasAttribute('cdkDragHandle') ||
        node.hasAttribute('cdkdraghandle') ||
        node.classList?.contains('cdk-drag-handle') ||
        node.classList?.contains('cdk-drag') ||
        node.draggable === true ||
        node.classList?.contains('ant-slider-handle') // slider handle'lar
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  // ═══════════════════════════════════════════════════
  //  TOUCH HANDLERS
  // ═══════════════════════════════════════════════════

  private _touchTarget: EventTarget | null = null;

  private _handleTouchStart(e: TouchEvent) {
    if (this._dialogIcindeMi(e.target) || this._dragAktifMi(e.target)) return;
    this._touchTarget = e.target;
    const touch = e.touches[0];
    this._pointerStart(touch.clientX, touch.clientY);
  }

  private _handleTouchMove(e: TouchEvent) {
    if (!this._isDragging) return;
    const touch = e.touches[0];
    const dx = touch.clientX - this._startX;
    const dy = touch.clientY - this._startY;

    // İç scroll kontrolü — child hala scroll edebiliyorsa biz karışmıyoruz
    if (this._icScrollKontrol(this._touchTarget, -dx, -dy)) {
      this._isDragging = false;
      return;
    }

    this._pointerMove(touch.clientX, touch.clientY, () => e.preventDefault());
  }

  // ═══════════════════════════════════════════════════
  //  MOUSE HANDLERS
  // ═══════════════════════════════════════════════════

  private _handleMouseDown(e: MouseEvent) {
    if (e.button !== 0) return;
    if (this._dialogIcindeMi(e.target) || this._dragAktifMi(e.target)) return;
    this._mouseTarget = true;
    this._touchTarget = e.target;
    this._pointerStart(e.clientX, e.clientY);
  }

  private _handleMouseMove(e: MouseEvent) {
    if (!this._mouseTarget || !this._isDragging) return;

    const dx = e.clientX - this._startX;
    const dy = e.clientY - this._startY;

    if (this._icScrollKontrol(this._touchTarget, -dx, -dy)) {
      this._isDragging = false;
      return;
    }

    // Drag-drop başladıysa (CDK preview oluşmuşsa) dur
    if (document.querySelector('.cdk-drag-preview')) {
      this._handlePointerEnd();
      return;
    }

    this._pointerMove(e.clientX, e.clientY, () => e.preventDefault());
  }

  // ═══════════════════════════════════════════════════
  //  WHEEL HANDLER
  // ═══════════════════════════════════════════════════

  private _handleWheel(e: WheelEvent) {
    // Dialog/overlay içinden geliyorsa yoksay
    if (this._dialogIcindeMi(e.target)) return;

    // İç scrollable child hala scroll edebiliyorsa yoksay
    if (this._icScrollKontrol(e.target, e.deltaX, e.deltaY)) return;

    let needsBounce = false;

    // Dikey
    if (this.yon === 'vertical' || this.yon === 'both') {
      const atTop = this.el.scrollTop <= 0;
      const atBottom = this.el.scrollTop + this.el.clientHeight >= this.el.scrollHeight - 1;

      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        this._wheelOverflowY += e.deltaY * -0.3;
        this._wheelOverflowY = this._rubberBand(this._wheelOverflowY);
        this._overflowY = this._wheelOverflowY;
        needsBounce = true;
      }
    }

    // Yatay
    if (this.yon === 'horizontal' || this.yon === 'both') {
      const atLeft = this.el.scrollLeft <= 0;
      const atRight = this.el.scrollLeft + this.el.clientWidth >= this.el.scrollWidth - 1;

      if ((atLeft && e.deltaX < 0) || (atRight && e.deltaX > 0)) {
        this._wheelOverflowX += e.deltaX * -0.3;
        this._wheelOverflowX = this._rubberBand(this._wheelOverflowX);
        this._overflowX = this._wheelOverflowX;
        needsBounce = true;
      }
    }

    if (needsBounce) {
      e.preventDefault();
      e.stopPropagation(); // Üst bounce-scroll'a taşmasını engelle
      this._applyTransform();

      clearTimeout(this._wheelTimer);
      this._wheelTimer = setTimeout(() => {
        this._wheelOverflowX = 0;
        this._wheelOverflowY = 0;
        this._springBack();
      }, 150);
    }
  }

  // ═══════════════════════════════════════════════════
  //  ORTAK POINTER İŞLEMLERİ
  // ═══════════════════════════════════════════════════

  private _pointerStart(x: number, y: number) {
    if (this._springRafId) {
      cancelAnimationFrame(this._springRafId);
      this._springRafId = 0;
    }
    this._startX = x;
    this._startY = y;
    this._isDragging = true;
    this._isBouncing = false;
  }

  private _pointerMove(x: number, y: number, preventDefault: () => void) {
    if (!this._isDragging) return;

    const dx = x - this._startX;
    const dy = y - this._startY;

    let needsBounce = false;

    // Dikey bounce
    if (this.yon === 'vertical' || this.yon === 'both') {
      const atTop = this.el.scrollTop <= 0;
      const atBottom = this.el.scrollTop + this.el.clientHeight >= this.el.scrollHeight - 1;

      if ((atTop && dy > 0) || (atBottom && dy < 0)) {
        this._overflowY = this._rubberBand(dy);
        needsBounce = true;
      } else if (this._isBouncing) {
        this._overflowY = 0;
      }
    }

    // Yatay bounce
    if (this.yon === 'horizontal' || this.yon === 'both') {
      const atLeft = this.el.scrollLeft <= 0;
      const atRight = this.el.scrollLeft + this.el.clientWidth >= this.el.scrollWidth - 1;

      if ((atLeft && dx > 0) || (atRight && dx < 0)) {
        this._overflowX = this._rubberBand(dx);
        needsBounce = true;
      } else if (this._isBouncing) {
        this._overflowX = 0;
      }
    }

    if (needsBounce) {
      this._isBouncing = true;
      preventDefault();
      this._applyTransform();
    } else if (this._isBouncing) {
      this._isBouncing = false;
      this._applyTransform();
    }
  }

  private _handlePointerEnd() {
    this._isDragging = false;
    this._mouseTarget = false;
    this._isBouncing = false;
    this._touchTarget = null;

    if (this._overflowX !== 0 || this._overflowY !== 0) {
      this._springBack();
    }
  }

  // ═══════════════════════════════════════════════════
  //  FİZİK — Rubber band + Spring-back
  // ═══════════════════════════════════════════════════

  private _rubberBand(delta: number): number {
    const sign = delta < 0 ? -1 : 1;
    const abs = Math.abs(delta);
    const clamped = this.bounceMax * (1 - Math.exp(-abs * this.bounceDamping / this.bounceMax));
    return sign * clamped;
  }

  private _springBack() {
    const startX = this._overflowX;
    const startY = this._overflowY;
    const startTime = performance.now();
    const duration = this.bounceSpeed;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);

      // Critically damped spring: f(t) = 1 - (1 + ωt) * e^(-ωt)
      const omega = 8;
      const spring = 1 - (1 + omega * t) * Math.exp(-omega * t);

      this._overflowX = startX * (1 - spring);
      this._overflowY = startY * (1 - spring);

      this._applyTransform();

      if (t < 1) {
        this._springRafId = requestAnimationFrame(animate);
      } else {
        this._overflowX = 0;
        this._overflowY = 0;
        this._applyTransform();
        this._springRafId = 0;
      }
    };

    if (this._springRafId) cancelAnimationFrame(this._springRafId);
    this._springRafId = requestAnimationFrame(animate);
  }

  // ─── Transform uygula ───

  private _applyTransform() {
    if (this._rafId) cancelAnimationFrame(this._rafId);

    this._rafId = requestAnimationFrame(() => {
      const tx = Math.round(this._overflowX * 10) / 10;
      const ty = Math.round(this._overflowY * 10) / 10;

      if (tx === 0 && ty === 0) {
        this.el.style.transform = '';
        this.el.style.willChange = '';
      } else {
        this.el.style.willChange = 'transform';
        this.el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }
      this._rafId = 0;
    });
  }
}
